import { t } from 'i18next'
import { z } from 'zod'

import { PlantaValidator } from '@/features/proposta/entities/InformacoesIniciaisStep/planta-validator'
import { VagaValidator } from '@/features/proposta/entities/InformacoesIniciaisStep/vaga-validator'
import { VendedorValidator } from '@/features/proposta/entities/InformacoesIniciaisStep/vendedor-validator'
import { ValidadorComprador } from '@/features/proposta/entities/ParticipacaoComprador/participacao-comprador-validador'
import { PropostaDtoMapper } from '@/features/proposta/entities/PropostaMapper/proposta-dto-mapper'
import {
  assinaturaContratoEnum,
  ClienteRegimeCasamentoEnum,
  formaPagamentoComissaoEnum,
  opcaoVagaEnum,
  tipoAssinaturaEnum,
  tipoDoCompradorEnum,
} from '@/features/proposta/interfaces/proposta.enums'
import type { TPropostaData } from '@/features/proposta/interfaces/proposta.type'
import { EstadoCivilEnum, origemTelefone, tipoDeCliente } from '@/shared/interfaces/cliente.enums'

export const simulacaoPropostaSchema = z.object({
  id: z.string(),
  cliente: z.object({
    id: z.string(),
    vendedorAlocadoId: z.string().nullish(),
    cpf: z.string().nullish(),
  }),
  empreendimento: z.object({
    idExterno: z.number(),
    nome: z.string(),
  }),
  valorPoupanca: z.number().nullish(),
  valorSaldo: z.number().nullish(),
  valorTotalSimulado: z.number().nullish(),
  entrada: z.array(
    z.object({
      valor: z.number(),
    }),
  ),
  mensais: z.array(
    z.object({
      parcelas: z.array(
        z.object({
          valor: z.number(),
        }),
      ),
    }),
  ),
  intermediarias: z.array(
    z.object({
      parcelas: z.array(
        z.object({
          valor: z.number(),
        }),
      ),
    }),
  ),
  posConclusao: z.array(
    z.object({
      valor: z.number(),
    }),
  ),
  intermediariasPosConclusao: z
    .array(
      z.object({
        parcelas: z.array(
          z.object({
            valor: z.number(),
          }),
        ),
      }),
    )
    .nullish(),
  produto: z.object({
    nome: z.string(),
    acessorioObrigatorio: z.boolean().nullish(),
    idExterno: z.number(),
    codigo: z.string(),
    tipoDoProduto: z.enum(['Unidade', 'Acessorio', 'Lote']),
    plantaId: z.string().nullish(),
    plantaDescricao: z.string().nullish(),
    dataMarcacaoAlvenaria: z.string().nullish(),
    acessoriosDaUnidade: z
      .array(
        z.object({
          nome: z.string(),
          acessorios: z.array(
            z.object({
              idExterno: z.number(),
              nome: z.string(),
            }),
          ),
        }),
      )
      .nullish(),
  }),
  bloco: z.object({
    idExterno: z.number(),
    nome: z.string(),
  }),
  tipoDeAmortizacao: z.string().nullish(),
  formaDePagamento: z.string().nullish(),
})

export type TSimulacaoProposta = z.input<typeof simulacaoPropostaSchema>

const informacoesIniciaisSchema = z
  .object({
    idExternoBloco: z.number().readonly(),
    vendedorAlocadoCliente: z.string().nullish().readonly(),
    clienteAtendimento: z.object({
      id: z.string(),
      cpf: z.string().nullish(),
    }),
    simulacaoUnidade: simulacaoPropostaSchema.nullish(),
    simulacaoLote: simulacaoPropostaSchema.nullish(),
    simulacoesAcessorios: z.array(simulacaoPropostaSchema),
    opcaoVaga: z.enum(opcaoVagaEnum).nullish(),
    vagaIds: z.array(
      z.object({
        id: z.number(),
        nomeVaga: z.string(),
        descricao: z.string(),
        substituiVagaPadrao: z.boolean().default(false),
      }),
    ),
    planta: z.object({
      id: z.string(),
      descricao: z.string(),
    }),
    vendedor: z
      .object({
        id: z.string(),
        possuiDadosDeComissao: z.boolean(),
      })
      .superRefine(({ id, possuiDadosDeComissao }, ctx) => {
        const vendedorError = new VendedorValidator({
          vendedorId: id,
          possuiDadosDeComissao: possuiDadosDeComissao,
        }).validate()
        if (vendedorError) {
          ctx.addIssue({
            code: 'custom',
            message: vendedorError,
            input: { id, possuiDadosDeComissao },
          })
        }
      }),
  })
  .superRefine(({ simulacaoUnidade, simulacoesAcessorios, planta, vagaIds }, ctx) => {
    const plantaError = new PlantaValidator({
      isUnidade: !!simulacaoUnidade,
      plantaId: planta.id,
    }).validate()

    const vagaError = new VagaValidator({
      simulacaoUnidade: simulacaoUnidade,
      vagaIds: vagaIds,
      possuiAcessoriosExtras: !!simulacoesAcessorios.length,
    }).validate()

    if (plantaError) {
      ctx.addIssue({
        path: ['planta'],
        code: 'custom',
        message: plantaError,
      })
    }

    if (vagaError) {
      ctx.addIssue({
        path: ['vagaIds'],
        code: 'custom',
        message: vagaError,
      })
    }
  })

const participacoesImobiliariasCorretoresSchema = z.discriminatedUnion('incluirParticipacoes', [
  z.object({
    incluirParticipacoes: z.literal('Sim'),
    formaPagamentoComissao: z.enum(formaPagamentoComissaoEnum).default(formaPagamentoComissaoEnum.PagaPelaPlaenge),
    participacoes: z
      .array(
        z.object({
          imobiliariaCorretor: z
            .object({
              idExterno: z.string(),
              descricao: z.string(),
              nome: z.string(),
              tipoImobiliariaCorretor: z.enum(['Imobiliaria', 'CorretorAutonomo', '']),
              documento: z.string(),
            })
            .refine(
              ({ idExterno }) => idExterno,
              t('proposta:detalhes_proposta.imobiliaria.errors.form.selecao_obrigatoria'),
            ),
          corretagem: z.discriminatedUnion('tipo', [
            z.object({
              tipo: z.literal('percentual'),
              percentualCorretagemImobiliaria: z.coerce
                .number<number>()
                .gt(0, t('proposta:detalhes_proposta.imobiliaria.errors.form.valor_invalido'))
                .lte(100, t('proposta:detalhes_proposta.imobiliaria.errors.form.valor_invalido')),
            }),
            z.object({
              tipo: z.literal('valor'),
              valorCorretagemImobiliaria: z
                .number()
                .gt(0, t('proposta:detalhes_proposta.imobiliaria.errors.form.valor_invalido')),
            }),
          ]),
        }),
      )
      .superRefine((participacoes, ctx) => {
        if (!participacoes.length) return z.NEVER
        const contagemImobiliariasContadores = new Map<string, number>()
        participacoes.forEach(({ imobiliariaCorretor }) =>
          contagemImobiliariasContadores.set(
            imobiliariaCorretor.idExterno,
            (contagemImobiliariasContadores.get(imobiliariaCorretor.idExterno) ?? 0) + 1,
          ),
        )
        participacoes.forEach(({ imobiliariaCorretor }, index) => {
          if ((contagemImobiliariasContadores.get(imobiliariaCorretor.idExterno) ?? 0) < 2) return
          ctx.addIssue({
            code: 'custom',
            message: t('proposta:detalhes_proposta.imobiliaria.errors.form.duplicado'),
            path: [index, 'imobiliariaCorretor'],
            input: imobiliariaCorretor,
          })
        })
      }),
  }),
  z.object({
    incluirParticipacoes: z.literal('Não'),
  }),
  z.object({
    incluirParticipacoes: z.literal('').refine(() => false),
  }),
])

const participacoesCompradoresSchema = z
  .object({
    titular: z.object({
      id: z.string(),
      percentualParticipacao: z.number(),
    }),
    compradores: z.array(
      z.object({
        id: z.string(),
        percentualParticipacao: z
          .number()
          .min(0.01, t('proposta:detalhes_proposta.participacao_comprador.erros.maior_zero'))
          .max(99.99, t('proposta:detalhes_proposta.participacao_comprador.erros.menor_cem')),
      }),
    ),
  })
  .superRefine(({ compradores, titular }, ctx) => {
    if (!compradores.length) return
    const erros = ValidadorComprador.validarParticipacoesCompradores(titular, compradores)
    if (!erros.length) return
    for (const erro of erros) {
      ctx.addIssue({
        code: 'custom',
        message: erro.message,
        path: erro.path,
      })
    }
  })

const vinculoSchema = z.object({
  clienteId: z.string(),
  tipo: z.enum(['Representante', 'MaePai', 'Conjuge']),
})

export const comprador = z.object({
  clienteId: z.string(),
  tipo: z.enum(tipoDoCompradorEnum),
  vinculos: z.array(vinculoSchema),
  tipoAssinatura: z.enum(tipoAssinaturaEnum).nullish(),
})

export const clientePessoaFisicaPropostaSchema = z.object({
  id: z.string(),
  tipoDoCliente: z.enum(tipoDeCliente),
  nome: z.string(),
  cpf: z.string(),
  email: z.string(),
  maiorDe18Anos: z.boolean(),
  nacionalidadeId: z.string(),
  profissaoId: z.string(),
  regimeCasamento: z.enum(ClienteRegimeCasamentoEnum).nullish(),
  estadoCivil: z.enum(EstadoCivilEnum).nullish(),
  imagem: z.string().nullish(),
  possuiUniaoEstavel: z.boolean().nullish(),
  telefone: z.object({
    numero: z.string(),
    principal: z.boolean(),
    origem: z.enum(origemTelefone),
  }),
})
export type TClientePessoaFisicaPropostaSchema = z.infer<typeof clientePessoaFisicaPropostaSchema>

export const clientePessoaJuridicaPropostaSchema = z.object({
  id: z.string(),
  tipo: z.literal(tipoDoCompradorEnum.PessoaJuridica),
  razaoSocial: z.string(),
  cnpj: z.string(),
  email: z.string(),
  telefone: z.object({
    numero: z.string(),
    principal: z.boolean(),
    origem: z.enum(origemTelefone),
  }),
})
export type TClientePessoaJuridicaPropostaSchema = z.infer<typeof clientePessoaJuridicaPropostaSchema>

const procuradorPropostaSchema = z.object({
  procuradorId: z.string(),
  clientesIds: z.array(z.string()),
})

export type TProcuradorPropostaSchema = z.infer<typeof procuradorPropostaSchema>

export type TCompradorPropostaSchema = z.infer<typeof comprador>
export type TCompradorPessoaJuridicaPropostaSchema = z.infer<typeof clientePessoaJuridicaPropostaSchema>

export const propostaSchema = z
  .object({
    step: z.number(),
    informacoesIniciais: informacoesIniciaisSchema,
    cadastroCompradores: z
      .object({
        clientes: z.array(clientePessoaFisicaPropostaSchema),
        clientesJuridicos: z.array(clientePessoaJuridicaPropostaSchema),
        compradores: z.array(comprador),
        procuradores: z.array(procuradorPropostaSchema),
      })
      .superRefine(({ compradores }, ctx) => {
        if (!compradores.length) {
          ctx.addIssue({
            code: 'custom',
            message: t('proposta:cadastro_compradores.erro.obrigatorio'),
          })
        }
      }),
    detalhesDaProposta: z.object({
      participacaoCompradores: participacoesCompradoresSchema,
      imobiliariasCorretores: participacoesImobiliariasCorretoresSchema,
      assinaturaContrato: z
        .union([z.enum(assinaturaContratoEnum), z.literal('')])
        .refine((assinatura) => assinatura !== '', t('proposta:detalhes_proposta.assinaturas.required_error')),
      termosDaProposta: z.string().min(1),
      observacoes: z.string().optional(),
      tipoEmolumento: z.enum(['AlienacaoFiduciaria', 'ContratoCompraVenda']).nullish(),
    }),
  })
  .superRefine(({ detalhesDaProposta, cadastroCompradores }, ctx) => {
    const compradores = cadastroCompradores.compradores
    if (compradores?.length <= 1) return
    const titular = detalhesDaProposta.participacaoCompradores.titular
    const erros = [
      ValidadorComprador.validarTitularObrigatorio(compradores, titular.id),
      ValidadorComprador.validarPercentualTitular(compradores, titular.percentualParticipacao),
    ]
    for (const erro of erros) {
      if (!erro) continue
      ctx.addIssue({ code: 'custom', ...erro, input: { compradores, titular } })
    }
  })
  .transform((data) => {
    const dto: TPropostaData = new PropostaDtoMapper(data).mapear()
    return dto
  })

export type TPropostaSchemaInput = z.input<typeof propostaSchema>
export type TPropostaSchemaOutput = z.output<typeof propostaSchema>

export type TParticipacao = Extract<
  TPropostaSchemaInput['detalhesDaProposta']['imobiliariasCorretores'],
  { incluirParticipacoes: 'Sim' }
>['participacoes'][number]

export type TCorretagem = Extract<
  TPropostaSchemaInput['detalhesDaProposta']['imobiliariasCorretores'],
  { incluirParticipacoes: 'Sim' }
>['participacoes'][number]['corretagem']
